// components/TheIntroduction.js
import React, { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { resizeImage, formatAvatarUrl } from 'lib/utils';
import { updateUserMetadata, changeUserProfileAvatar } from 'lib/user'
import blankprofileImage from 'images/blank-profile-picture.png'
import AuthContext from 'app/AuthContext';
import { sendEventToAnalytics } from 'lib/reporting';
import classes from "./OnboardingComponents.module.scss"




//Boilerplate code
const TheIntroduction = (props) => {

    const { userId } = useParams();

    const [profile, setProfile] = useState('');
    const [profileAvatar, setProfileAvatar] = useState(props.avatar ? formatAvatarUrl(props.avatar.url, props.avatar.updated_ts) : blankprofileImage);
    const [profileIntro, setProfileIntro] = useState(profile.intro_summary || '');
    
    // This will hold the user's profile image
    const [loading, setLoading] = useState(false);
    const [failedLoading, setFailedLoading] = useState(false);
    const [displayedError, setDisplayedError] = useState('');

    const { authToken, userMetadata, logout, forceRefresh } = useContext(AuthContext);

    const loggedInUserId = userMetadata.id;

    const [errors, setErrors] = useState({});

    /**
         * The function `uploadAvatar` is an asynchronous function that handles the uploading of a file,
         * specifically an avatar image, and updates the user's profile with the new avatar image.
         * @param event - The `event` parameter is an object that represents the event that triggered the
         * file upload. It is typically an event object that is passed to an event handler function when a
         * file is selected for upload. The `event.target` property refers to the element that triggered
         * the event, and `event.target.files
         */
    const uploadAvatar = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (process.env.REACT_APP_ENVIRONMENT !== "dev") { await sendEventToAnalytics("User", "user_change_avatar"); }
            setLoading(true);

            // Resize image, expect File object in return, not an actual file/image
            const resizedFileData = await resizeImage(file);

            let avatarId = null;
            // Add avatar ID
            if (typeof profile.user_avatar != 'undefined'
                && profile.user_avatar
                && profile.user_avatar.id) {
                avatarId = profile.user_avatar.id;
            }

            // Upload image
            const { isSuccess: changeAvatarSuccess, resp: changeAvatarResp, err: changeAvatarErr } =
                await changeUserProfileAvatar(userId, avatarId, resizedFileData, authToken);

            if (changeAvatarSuccess) {
                if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("Profile Image Upload || Success", changeAvatarResp); }

                const updatedProfile = { ...profile, user_avatar: changeAvatarResp.results.user_avatar };
                
                setProfile(updatedProfile);
                sessionStorage.setItem('profile', JSON.stringify(updatedProfile));

                //Updates image on the large profile circle
                const updatedAvatar = formatAvatarUrl(changeAvatarResp.results.user_avatar.url, changeAvatarResp.results.user_avatar.updated_ts);
                setProfileAvatar(updatedAvatar);

                // Update the profile image in the nav bar
                updateUserMetadata(changeAvatarResp.results.user_avatar, 'user_avatar');
                forceRefresh();
                setLoading(false);
                setFailedLoading(false);
            } else {
                if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.error("Error uploading profile image:", changeAvatarErr); }
                if (changeAvatarResp) {
                    // If unauthorized, log out the user's session
                    if (changeAvatarResp.status_code === "UNAUTHORIZED") { logout(); }
                } else {
                    if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.error("Empty response:", changeAvatarErr); }
                }
                setLoading(false);
                setFailedLoading(true);
            }
        } else {
            setFailedLoading(true);
        }
    };

    //Autofills input field if user already has introduction data
    useEffect(() => {
        const holder = JSON.parse(sessionStorage.getItem('profile'));
        const updatedProfile = { 
            ...holder, 
            intro: holder && holder.intro ? holder.intro : props.intro,
            user_avatar: holder && holder.user_avatar ? holder.user_avatar : props.avatar 
        }
        setProfile(updatedProfile);
        setProfileIntro(updatedProfile.intro || props.intro);
        setProfileAvatar(updatedProfile.user_avatar && updatedProfile.user_avatar.url ? formatAvatarUrl(updatedProfile.user_avatar.url, updatedProfile.user_avatar.updated_ts) : props.avatar ? formatAvatarUrl(props.avatar.url, props.avatar.updated_ts) : blankprofileImage);
        sessionStorage.setItem('profile', JSON.stringify(updatedProfile))
    }, [props.intro, props.avatar])

    //Updates sessionstorage with Introduction input
    const handleProfileIntroChange = (event) => {
        const updatedProfile = { ...profile, intro: event.target.value };
        setProfileIntro(event.target.value);
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.profileIntro;
            return newErrors;
        });

        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    };

    return (
        <div className={classes.theIntroductionContainer}>
            <h1 className={classes.title}>Differentiate yourself</h1>
            <div className={classes.subtitle}>Upload your profile avatar and introduce yourself.</div>
            <div className={classes.profilePicSubsection}>
                <div className={classes.largeProfileCircle} style={{ backgroundImage: `url(${profileAvatar})` }} />
                {authToken && userMetadata && loggedInUserId === userId && (
                    <div className={classes.profilePicSubsectionButton}>
                        <div className={classes.profilePictureWrapper}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={uploadAvatar}
                                style={{ display: 'none' }}
                                id="fileUploader"
                            />
                            <label htmlFor="fileUploader" className={classes.uploadButton}>
                                {loading ? 'Uploading...' : (failedLoading ? 'Failed, try again.' : 'Update Avatar')}
                            </label>
                        </div>
                        {displayedError}
                    </div>
                )}
            </div>
            <div className={classes.introText}>Introduction</div>
            <div className={classes.profileDescriptionCard}>
                <div className={classes.textAreaWrapper}>
                    <textarea
                        name="profile-intro"
                        rows="5"
                        cols="40"
                        maxLength="350"
                        value={profileIntro}
                        placeholder={"- Your personal mission and next milestone. \n" + "- What makes you stand out? \n" + "- A gift for the community. \n" + "- Something people have to know about you."}
                        onChange={handleProfileIntroChange}
                        style={{ resize: 'none' , fontSize: '16px'}}
                    ></textarea>
                    <span className={classes.largeCharCounter}>{profileIntro.length}/350</span>
                </div>
            </div>
        </div>
    );
};

export default TheIntroduction;